<template>
  <v-card class="outlined-card pa-5 d-flex flex-column" elevation="0" style="height: 100%">
    <div class="d-flex justify-space-between align-center mb-4">
      <h3 class="text-subtitle-1 font-weight-bold text-uppercase" style="color: #202A63;">
        Thông báo nhà trường
      </h3>
    </div>

    <div class="flex-grow-1">
      <div v-if="loading" class="text-center py-4">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      <div v-else-if="!newsList.length" class="text-center py-4 grey--text">
        Chưa có thông báo nào
      </div>
      <template v-else>
        <div v-for="(news) in newsList" :key="news.id">
          <div class="mb-5">
            <div class="d-flex justify-space-between align-start mb-1">
              <div>
                <div class="font-weight-medium text-body-2" style="color: #202A63; font-size: 14px;">
                  {{ news.senderMethod === 'email' ? 'EMAIL' : 'APP' }}
                  <span v-if="getPostToType(news)"> - {{ getPostToType(news) }}</span>
                </div>
                <div class="text-caption grey--text">{{ formatDate(news.createdAt) }}</div>
              </div>
              <a href="#" @click.prevent="openDetail(news)" class="text-body-2 blue--text font-weight-medium text-decoration-none">Xem chi tiết</a>
            </div>
            <div v-if="news.title" class="font-weight-bold text-body-2 mt-2 mb-1" style="color: #202A63;">{{ news.title }}</div>
            <div class="text-body-2" style="color: #202A63;">{{ getPreview(news.content) }}</div>
          </div>
          <v-divider class="mb-4"></v-divider>
        </div>
      </template>
    </div>

    <div class="d-flex justify-center mt-4" v-if="totalPages > 1">
      <div class="d-flex align-center text-caption font-weight-medium" style="gap: 12px; color: #757575;">
        <v-icon small :color="currentPage > 1 ? 'blue' : 'grey'" class="page-nav" @click="goToPage(currentPage - 1)">mdi-chevron-left</v-icon>
        <span
          v-for="(p, i) in displayedPages"
          :key="i"
          :class="p === '...' ? 'page-ellipsis' : ['page-item', { active: p === currentPage }]"
          @click="p !== '...' && goToPage(p)"
        >{{ p }}</span>
        <v-icon small :color="currentPage < totalPages ? 'blue' : 'grey'" class="page-nav" @click="goToPage(currentPage + 1)">mdi-chevron-right</v-icon>
      </div>
    </div>

    <!-- Dialog xem chi tiết -->
    <v-dialog v-model="dialog" max-width="600px" scrollable>
      <v-card v-if="selectedNews">
        <v-card-title v-if="selectedNews.title" class="text-h6" style="color: #202A63; word-break: break-word;">
          {{ selectedNews.title }}
        </v-card-title>
        <v-card-title v-else class="text-h6" style="color: #202A63; word-break: break-word;">
          Thông báo
        </v-card-title>
        <v-card-subtitle class="mt-1">
          Ngày gửi: {{ formatDate(selectedNews.createdAt) }}
        </v-card-subtitle>
        <v-divider></v-divider>
        <v-card-text class="pt-4" style="color: #424242;">
          <div v-html="selectedNews.content"></div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="dialog = false">Đóng</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import moment from 'moment'
import { Post } from '@/plugins/api'

const PAGE_SIZE = 5

export default {
  name: 'DashboardAnnouncements',
  data: () => ({
    newsList: [],
    loading: false,
    dialog: false,
    selectedNews: null,
    currentPage: 1,
    totalCount: 0,
  }),
  computed: {
    totalPages() {
      return Math.ceil(this.totalCount / PAGE_SIZE)
    },
    displayedPages() {
      const total = this.totalPages
      const cur = this.currentPage
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
      const pages = []
      if (cur <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i)
        pages.push('...')
        pages.push(total)
      } else if (cur >= total - 3) {
        pages.push(1)
        pages.push('...')
        for (let i = total - 4; i <= total; i++) pages.push(i)
      } else {
        pages.push(1)
        pages.push('...')
        for (let i = cur - 1; i <= cur + 1; i++) pages.push(i)
        pages.push('...')
        pages.push(total)
      }
      return pages
    }
  },
  async created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const params = {
          student_null: true,
          type_ne: 'attendance',
          _limit: PAGE_SIZE,
          _start: (this.currentPage - 1) * PAGE_SIZE,
          _sort: 'createdAt:DESC',
        }
        const [data, count] = await Promise.all([
          Post.fetch(params).catch(() => []),
          Post.count({ student_null: true, type_ne: 'attendance' }).catch(() => 0),
        ])
        this.newsList = data || []
        this.totalCount = typeof count === 'number' ? count : (count ? Number(count) : 0)
      } catch (error) {
        console.error('Error fetching news:', error)
      } finally {
        this.loading = false
      }
    },
    async goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return
      this.currentPage = page
      await this.fetchData()
    },
    formatDate(date) {
      if (!date) return ''
      return moment(date).format('HH:mm, DD/MM/YYYY')
    },
    getPreview(html) {
      if (!html) return ''
      const text = html.replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, ' ')
      return text.length > 100 ? text.substring(0, 100) + '...' : text
    },
    openDetail(news) {
      this.selectedNews = news
      this.dialog = true
    },
    getPostToType(p) {
      if (p.school) return 'Toàn trường'
      else if (p.grade) return p.grade.title
      else if (p.class) return `Lớp ${p.class.title}`
      return ''
    }
  }
}
</script>

<style scoped>
.outlined-card {
  border: 1px solid #dde3f0 !important;
  border-radius: 16px !important;
  box-shadow: 0 2px 12px rgba(25, 50, 120, 0.08) !important;
}

.page-item {
  padding: 2px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.page-item:hover {
  background-color: #2196F3;
  color: white !important;
}

.page-item.active {
  border: 1px solid #2196F3;
  color: #2196F3;
}

.page-nav {
  cursor: pointer;
}
.page-nav:hover {
  color: #2196F3 !important;
}

.page-ellipsis {
  color: #9e9e9e;
  padding: 2px 4px;
}
</style>
